/** Tiny allocation-light Vec3 helpers (plain number triples, no three.js). */
import type { Vec3 } from "./types.ts";

export const V0: Vec3 = [0, 0, 0];

export function v(x = 0, y = 0, z = 0): Vec3 {
  return [x, y, z];
}

export function add(a: Vec3, b: Vec3): Vec3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}

export function sub(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

export function mul(a: Vec3, s: number): Vec3 {
  return [a[0] * s, a[1] * s, a[2] * s];
}

export function addTo(target: Vec3, a: Vec3): Vec3 {
  target[0] += a[0];
  target[1] += a[1];
  target[2] += a[2];
  return target;
}

export function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

export function cross(a: Vec3, b: Vec3): Vec3 {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

export function len(a: Vec3): number {
  return Math.hypot(a[0], a[1], a[2]);
}

export function dist(a: Vec3, b: Vec3): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
}

/** Horizontal (XZ) distance. */
export function distXZ(a: Vec3, b: Vec3): number {
  return Math.hypot(a[0] - b[0], a[2] - b[2]);
}

export function normalize(a: Vec3): Vec3 {
  const l = len(a);
  return l > 1e-9 ? [a[0] / l, a[1] / l, a[2] / l] : [0, 0, 0];
}

export function lerp(a: Vec3, b: Vec3, s: number): Vec3 {
  return [a[0] + (b[0] - a[0]) * s, a[1] + (b[1] - a[1]) * s, a[2] + (b[2] - a[2]) * s];
}

export function mid(a: Vec3, b: Vec3): Vec3 {
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2];
}

export function clone(a: Vec3): Vec3 {
  return [a[0], a[1], a[2]];
}

/** Shortest distance from point p to segment ab. */
export function pointSegmentDistance(p: Vec3, a: Vec3, b: Vec3): number {
  const ab = sub(b, a);
  const l2 = dot(ab, ab);
  if (l2 < 1e-12) return dist(p, a);
  let t = dot(sub(p, a), ab) / l2;
  t = Math.max(0, Math.min(1, t));
  return dist(p, [a[0] + ab[0] * t, a[1] + ab[1] * t, a[2] + ab[2] * t]);
}

/**
 * Second derivative from three non-uniformly spaced samples
 * (exact for a quadratic through p0,p1,p2).
 */
export function secondDerivative(
  p0: Vec3,
  p1: Vec3,
  p2: Vec3,
  dt1: number,
  dt2: number,
): Vec3 {
  const d = Math.max(1e-6, (dt1 + dt2) / 2);
  const out: Vec3 = [0, 0, 0];
  for (let i = 0; i < 3; i++) {
    const v1 = (p1[i] - p0[i]) / Math.max(1e-6, dt1);
    const v2 = (p2[i] - p1[i]) / Math.max(1e-6, dt2);
    out[i] = (v2 - v1) / d;
  }
  return out;
}

/** Central first derivative across three non-uniformly spaced samples. */
export function centralDerivative(p0: Vec3, p2: Vec3, dtTotal: number): Vec3 {
  const d = Math.max(1e-6, dtTotal);
  return [(p2[0] - p0[0]) / d, (p2[1] - p0[1]) / d, (p2[2] - p0[2]) / d];
}
